import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Clock, MapPin, Users, AlertCircle, CheckCircle, X } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

const WaitlistManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");

  // البيانات التجريبية
  const waitlistEntries = [
    {
      id: "1",
      clientName: "أحمد محمد",
      destinationType: "country",
      destinationName: "تركيا",
      serviceType: "package",
      checkInDate: "2025-02-15",
      checkOutDate: "2025-02-22",
      passengerCount: 4,
      budgetMin: 8000,
      budgetMax: 12000,
      currency: "SAR",
      priority: 2,
      status: "active",
      createdAt: "2025-01-20",
      contactPreferences: { email: true, sms: true, whatsapp: false },
      specialRequirements: "غرف عائلية مع إطلالة على البحر"
    },
    {
      id: "2", 
      clientName: "فاطمة علي",
      destinationType: "city",
      destinationName: "دبي",
      serviceType: "flight",
      checkInDate: "2025-03-01",
      checkOutDate: "2025-03-05",
      passengerCount: 2,
      budgetMin: 2000,
      budgetMax: 3000,
      currency: "SAR",
      priority: 3,
      status: "active",
      createdAt: "2025-01-18",
      contactPreferences: { email: true, sms: false, whatsapp: true },
      specialRequirements: "تذاكر طيران فقط - مقاعد نافذة"
    },
    {
      id: "3",
      clientName: "خالد الأحمد",
      destinationType: "country", 
      destinationName: "ماليزيا",
      serviceType: "hotel",
      checkInDate: "2025-02-10",
      checkOutDate: "2025-02-17",
      passengerCount: 3,
      budgetMin: 5000,
      budgetMax: 7000,
      currency: "SAR",
      priority: 1,
      status: "fulfilled",
      createdAt: "2025-01-15",
      contactPreferences: { email: true, sms: true, whatsapp: true },
      specialRequirements: "فندق 5 نجوم قريب من الأسواق"
    }
  ];

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 3: return "bg-destructive/10 text-destructive border-destructive/20";
      case 2: return "bg-warning/10 text-warning border-warning/20";
      case 1: return "bg-success/10 text-success border-success/20";
      default: return "bg-muted";
    }
  };

  const getPriorityText = (priority: number) => {
    switch (priority) {
      case 3: return "عاجل";
      case 2: return "مهم";
      case 1: return "عادي";
      default: return "غير محدد";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-primary/10 text-primary border-primary/20";
      case "fulfilled": return "bg-success/10 text-success border-success/20";
      case "cancelled": return "bg-destructive/10 text-destructive border-destructive/20";
      case "expired": return "bg-muted/50 text-muted-foreground border-muted/20";
      default: return "bg-muted";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active": return "نشط";
      case "fulfilled": return "تم التنفيذ";
      case "cancelled": return "ملغي";
      case "expired": return "منتهي الصلاحية";
      default: return status;
    }
  };

  const getServiceTypeText = (type: string) => {
    switch (type) {
      case "package": return "باقة سياحية";
      case "flight": return "رحلة طيران";
      case "hotel": return "حجز فندق";
      case "transfer": return "نقل";
      default: return type;
    }
  };

  const getServiceIcon = (type: string) => {
    switch (type) {
      case "package": return "📦";
      case "flight": return "✈️";
      case "hotel": return "🏨";
      case "transfer": return "🚗";
      default: return "📋";
    }
  };

  const filteredEntries = waitlistEntries.filter(entry => {
    const matchesSearch = entry.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.destinationName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || entry.status === statusFilter;
    const matchesService = serviceFilter === "all" || entry.serviceType === serviceFilter;
    return matchesSearch && matchesStatus && matchesService;
  });

  const activeEntries = waitlistEntries.filter(entry => entry.status === "active").length;
  const fulfilledEntries = waitlistEntries.filter(entry => entry.status === "fulfilled").length;
  const highPriorityEntries = waitlistEntries.filter(entry => entry.priority === 3 && entry.status === "active").length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">إدارة قوائم الانتظار</h1>
          <p className="text-muted-foreground mt-1">إدارة طلبات العملاء للرحلات غير المتوفرة</p>
        </div>
        <Button className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          إضافة طلب انتظار
        </Button>
      </div>

      {/* إحصائيات سريعة */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">طلبات نشطة</p>
                <p className="text-2xl font-bold text-primary">{activeEntries}</p>
              </div>
              <Clock className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">تم التنفيذ</p>
                <p className="text-2xl font-bold text-success">{fulfilledEntries}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">عاجل</p>
                <p className="text-2xl font-bold text-destructive">{highPriorityEntries}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">معدل النجاح</p>
                <p className="text-2xl font-bold text-foreground">33%</p>
              </div>
              <div className="text-2xl">📈</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* أدوات البحث والفلترة */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="البحث بالاسم أو الوجهة..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="الحالة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="active">نشط</SelectItem>
                <SelectItem value="fulfilled">تم التنفيذ</SelectItem>
                <SelectItem value="cancelled">ملغي</SelectItem>
                <SelectItem value="expired">منتهي الصلاحية</SelectItem>
              </SelectContent>
            </Select>

            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger>
                <SelectValue placeholder="نوع الخدمة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الخدمات</SelectItem>
                <SelectItem value="package">باقة سياحية</SelectItem>
                <SelectItem value="flight">رحلة طيران</SelectItem>
                <SelectItem value="hotel">حجز فندق</SelectItem>
                <SelectItem value="transfer">نقل</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" className="w-full">
              تصدير القائمة
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* قائمة طلبات الانتظار */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            طلبات الانتظار
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredEntries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>لا توجد طلبات انتظار مطابقة للبحث</p>
              </div>
            ) : (
              filteredEntries.map((entry) => (
                <Card key={entry.id} className="border border-border hover:border-primary/20 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="text-2xl">
                          {getServiceIcon(entry.serviceType)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-foreground">{entry.clientName}</h3>
                            <Badge className={getPriorityColor(entry.priority)}>
                              {getPriorityText(entry.priority)}
                            </Badge>
                            <Badge className={getStatusColor(entry.status)}>
                              {getStatusText(entry.status)}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {entry.destinationName}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {entry.passengerCount} مسافر
                            </span>
                            <span>
                              {getServiceTypeText(entry.serviceType)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-left">
                        <p className="text-sm text-muted-foreground">الميزانية</p>
                        <p className="font-semibold text-foreground">
                          {entry.budgetMin.toLocaleString()} - {entry.budgetMax.toLocaleString()} {entry.currency}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">تاريخ السفر</p>
                        <p className="font-medium">
                          {format(new Date(entry.checkInDate), "dd MMM", { locale: ar })} - 
                          {format(new Date(entry.checkOutDate), "dd MMM yyyy", { locale: ar })}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">تاريخ الطلب</p>
                        <p className="font-medium">
                          {format(new Date(entry.createdAt), "dd MMMM yyyy", { locale: ar })}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">وسائل التواصل</p>
                        <div className="flex gap-1">
                          {entry.contactPreferences.email && <span>📧</span>}
                          {entry.contactPreferences.sms && <span>📱</span>}
                          {entry.contactPreferences.whatsapp && <span>📲</span>}
                        </div>
                      </div>
                    </div>

                    {entry.specialRequirements && (
                      <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">متطلبات خاصة:</p>
                        <p className="text-sm">{entry.specialRequirements}</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline">
                        عرض التفاصيل
                      </Button>
                      <Button size="sm" variant="outline">
                        إرسال عرض
                      </Button>
                      <Button size="sm" variant="outline">
                        تحديث الحالة
                      </Button>
                      {entry.status === "active" && (
                        <Button size="sm" variant="outline">
                          إشعار العميل
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WaitlistManagement;